import { useState } from 'react';
import {
  Plus,
  Save,
  Trash2,
  Edit2,
  Check,
  X,
  AlertCircle,
  Zap,
  Clock,
  ArrowRight,
  Settings,
} from 'lucide-react';
import { Button, Card, Spinner, ConfirmDialog } from '@centraoneplus/ui';
import { pipelineService } from '@/services/pipeline.service';
import type { Pipeline, PipelineWorkflowConfig } from '@/types/pipeline';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface WorkflowTabProps {
  pipeline: Pipeline;
  onUpdate: (updatedPipeline: Pipeline) => void;
}

interface AutomationRule {
  id: string;
  name: string;
  trigger: string;
  conditions: unknown[];
  actions: unknown[];
  enabled: boolean;
}

const defaultWorkflowConfig: PipelineWorkflowConfig = {
  automationRules: [],
};

const TRIGGERS = [
  { value: 'ticket_created', label: 'Ticket Created', description: 'When a new ticket is created' },
  { value: 'ticket_updated', label: 'Ticket Updated', description: 'When a ticket is modified' },
  { value: 'stage_changed', label: 'Stage Changed', description: 'When ticket moves to a new stage' },
  { value: 'priority_changed', label: 'Priority Changed', description: 'When ticket priority changes' },
  { value: 'assigned', label: 'Ticket Assigned', description: 'When ticket is assigned to an agent' },
  { value: 'sla_breach', label: 'SLA Breach Warning', description: 'When SLA is about to be breached' },
];

const ACTIONS = [
  { value: 'assign', label: 'Assign to Agent/Team' },
  { value: 'change_priority', label: 'Change Priority' },
  { value: 'add_tag', label: 'Add Tag' },
  { value: 'send_notification', label: 'Send Notification' },
  { value: 'send_email', label: 'Send Email' },
  { value: 'move_stage', label: 'Move to Stage' },
];

export default function WorkflowTab({ pipeline, onUpdate }: WorkflowTabProps) {
  // Merge with defaults to ensure all nested objects exist
  const [config, setConfig] = useState<PipelineWorkflowConfig>(() => {
    const raw = pipeline.workflowConfig;
    return {
      automationRules: raw?.automationRules || [],
    };
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    trigger: 'ticket_created',
    enabled: true,
  });

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await pipelineService.updateWorkflowConfig(pipeline.id, config);
      onUpdate({ ...pipeline, workflowConfig: config });
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving workflow config:', err);
      setError('Failed to save workflow configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateRule = () => {
    if (!ruleForm.name.trim()) return;

    const newRule: AutomationRule = {
      id: `rule-${Date.now()}`,
      name: ruleForm.name,
      trigger: ruleForm.trigger,
      conditions: [],
      actions: [],
      enabled: ruleForm.enabled,
    };

    setConfig({
      ...config,
      automationRules: [...config.automationRules, newRule],
    });
    setHasChanges(true);
    setIsCreating(false);
    setRuleForm({ name: '', trigger: 'ticket_created', enabled: true });
  };

  const handleDeleteRule = (ruleId: string) => setRuleToDelete(ruleId);

  const performDeleteRule = () => {
    if (ruleToDelete === null) return;

    setConfig({
      ...config,
      automationRules: config.automationRules.filter((r) => r.id !== ruleToDelete),
    });
    setHasChanges(true);
    setRuleToDelete(null);
  };

  const handleToggleRule = (ruleId: string) => {
    setConfig({
      ...config,
      automationRules: config.automationRules.map((r) =>
        r.id === ruleId ? { ...r, enabled: !r.enabled } : r
      ),
    });
    setHasChanges(true);
  };

  return (
    <>
    <div className="space-y-6">
      {/* Automation Overview */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Zap className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Workflow Automation</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Create rules to automate ticket handling and notifications
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="p-4 bg-gray-50 rounded-lg text-center dark:bg-gray-900">
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{config.automationRules.length}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Rules</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg text-center">
            <p className="text-2xl font-semibold text-green-600">
              {config.automationRules.filter((r) => r.enabled).length}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Active</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg text-center dark:bg-gray-900">
            <p className="text-2xl font-semibold text-gray-400 dark:text-gray-500">
              {config.automationRules.filter((r) => !r.enabled).length}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Disabled</p>
          </div>
        </div>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Create Rule Form */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Automation Rules</h4>
        <Button onClick={() => setIsCreating(true)} disabled={isCreating} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Rule
        </Button>
      </div>

      {isCreating && (
        <Card className="p-4 border-2 border-primary-200 bg-primary-50">
          <h4 className="text-sm font-medium text-gray-900 mb-4 dark:text-gray-100">Create Automation Rule</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">Rule Name *</label>
              <input
                type="text"
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                placeholder="e.g., Auto-assign urgent tickets"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">Trigger</label>
              <ThemedSelect
                value={ruleForm.trigger}
                onChange={(e) => setRuleForm({ ...ruleForm, trigger: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              >
                {TRIGGERS.map((trigger) => (
                  <option key={trigger.value} value={trigger.value}>
                    {trigger.label}
                  </option>
                ))}
              </ThemedSelect>
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                {TRIGGERS.find((t) => t.value === ruleForm.trigger)?.description}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ruleEnabled"
                checked={ruleForm.enabled}
                onChange={(e) => setRuleForm({ ...ruleForm, enabled: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:border-gray-700"
              />
              <label htmlFor="ruleEnabled" className="text-sm text-gray-700 dark:text-gray-200">
                Enable this rule immediately
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setIsCreating(false);
                  setRuleForm({ name: '', trigger: 'ticket_created', enabled: true });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateRule} disabled={!ruleForm.name.trim()}>
                <Check className="h-4 w-4 mr-1" />
                Create Rule
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Rules List */}
      <div className="space-y-3">
        {config.automationRules.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-4 dark:bg-gray-800">
              <Zap className="h-6 w-6 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-gray-500 mb-4 dark:text-gray-400">No automation rules configured</p>
            <p className="text-xs text-gray-400 mb-4 dark:text-gray-500">
              Create rules to automate repetitive tasks like assigning tickets, sending notifications, etc.
            </p>
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create your first rule
            </Button>
          </Card>
        ) : (
          config.automationRules.map((rule) => (
            <Card key={rule.id} className={`p-4 ${!rule.enabled ? 'opacity-60' : ''}`}>
              <div className="flex items-center gap-4">
                <div
                  className={`p-2 rounded-lg ${rule.enabled ? 'bg-green-100' : 'bg-gray-100'} dark:bg-gray-800`}
                >
                  <Zap className={`h-5 w-5 ${rule.enabled ? 'text-green-600' : 'text-gray-400'} dark:text-gray-500`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100">{rule.name}</h5>
                    {!rule.enabled && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                        Disabled
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400">
                    <Clock className="h-3 w-3" />
                    <span>{TRIGGERS.find((t) => t.value === rule.trigger)?.label}</span>
                    <ArrowRight className="h-3 w-3" />
                    <span>{rule.actions.length || 0} actions</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleRule(rule.id)}
                    title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRule(rule.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Conditions and actions would be expanded here in a full implementation */}
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Conditions and actions editor coming in next update
                </p>
              </div>
            </Card>
          ))
        )}
      </div>

      {hasChanges && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Workflow Configuration
              </>
            )}
          </Button>
        </div>
      )}
    </div>

    <ConfirmDialog
      isOpen={ruleToDelete !== null}
      onClose={() => setRuleToDelete(null)}
      onConfirm={performDeleteRule}
      title="Delete automation rule"
      message="Are you sure you want to delete this automation rule?"
      confirmLabel="Delete"
      cancelLabel="Cancel"
      variant="danger"
    />
    </>
  );
}
